import { INDUSTRIES } from '@/landing/content'
import { ContentPage, contentMeta, type ContentPageData } from '@/site/content'

const sample = INDUSTRIES.find((i) => i.key === 'dental')!

const page: ContentPageData = {
  path: '/dlya/stomatologii',
  parent: { name: 'Галузі', path: '/dlya' },
  crumb: 'Стоматології й клініки',
  title: 'AI-адміністратор для стоматології і клініки | Anytel',
  description:
    'AI-адміністратор приймає дзвінки пацієнтів 24/7: записує до лікаря у вільний час, відповідає про ціни й підготовку, складні питання передає реєстратурі.',
  eyebrow: 'Для стоматологій і медичних клінік',
  h1: 'AI-адміністратор для стоматології та медичної клініки',
  lead:
    'Пацієнти телефонують, коли їм зручно: о сьомій ранку, в обідню перерву, у неділю ввечері. AI-агент відповідає на кожен дзвінок українською, записує до потрібного лікаря у вільне вікно і передає реєстратурі те, що потребує людини.',
  sections: [
    {
      h2: 'Де клініка втрачає пацієнтів на телефоні',
      blocks: [
        'Адміністратор клініки робить усе одночасно: зустрічає пацієнтів, приймає оплату, телефонує тим, хто записаний на завтра. Коли телефон дзвонить саме в цю хвилину, вибір простий: або людина біля стійки, або людина на лінії.',
        {
          list: [
            'Дзвінок під час оплати чи консультації лишається без відповіді, і пацієнт набирає наступну клініку з пошуку.',
            'Увечері й у вихідні реєстратура не працює, хоча саме тоді люди мають час подумати про лікування.',
            'Багато розмов однакові: скільки коштує чистка, чи приймає педіатр у суботу, як підготуватися до аналізів. Вони забирають час, якого бракує на пацієнтів у клініці.',
          ],
        },
        'Скільки дзвінків пропускає саме ваша клініка, легко перевірити за тиждень. Як це зробити, ми розписали в [окремій статті](/blog/skilky-dzvinkiv-vy-propuskayete).',
      ],
    },
    {
      h2: 'Що робить AI-адміністратор',
      blocks: [
        { h3: 'Записує до лікаря у вільний час' },
        'Агент бачить графіки лікарів і кабінетів у календарі Anytel: робочі години, вихідні, зайняті вікна. Він пропонує пацієнту найближчий вільний час і записує одразу, без «вам передзвонять». Двох пацієнтів на один час до одного лікаря система не запише.',
        { h3: 'Відповідає про послуги, ціни й підготовку' },
        'Ви заповнюєте базу знань: послуги, ціни, адреси філій, як підготуватися до процедури, що взяти з собою. Агент відповідає саме за нею і не вигадує того, чого там немає: скаже, що уточнить, і передасть питання адміністратору.',
        { h3: 'Передає людині, коли це потрібно' },
        'Гострий біль, ускладнення після лікування, скарга, питання, на яке немає відповіді в базі знань. Ви самі визначаєте, коли агент переводить дзвінок на реєстратуру чи в чергу операторів, а коли приймає заявку на зворотний дзвінок.',
        { h3: 'Не ставить діагнозів' },
        'Агент не консультує з медичних питань і не призначає лікування. Його завдання: записати пацієнта, відповісти на організаційні питання і вчасно передати розмову людині. Для невідкладних випадків в інструкції агента прописують одне правило: порадити телефонувати 103.',
      ],
    },
  ],
  dialog: {
    h2: 'Приклад розмови',
    context: 'Стоматологія, субота, 9:12',
    dialog: sample.dialog,
    result: sample.result,
  },
  after: [
    {
      h2: 'Як підключити клініку',
      blocks: [
        {
          steps: [
            { title: 'Реєстрація і тестовий дзвінок', text: 'Створіть кабінет, оберіть шаблон для клініки і подзвоніть своєму агенту прямо з браузера. Номер для цього не потрібен.' },
            { title: 'Знання і графіки лікарів', text: 'Додайте послуги, ціни й відповіді на часті питання, створіть календарі лікарів з робочими годинами. Якщо зручніше, перший сценарій налаштуємо разом.' },
            { title: 'Переадресація', text: 'Увімкніть переадресацію з наявного номера клініки або підключіть свою АТС. Номер, який знають пацієнти, лишається тим самим.' },
            { title: 'Сповіщення', text: 'Оберіть, куди надходять нові записи: у Telegram адміністратора, на пошту, у вашу CRM.' },
          ],
        },
        'Переадресацію можна вмикати поступово: спершу лише на вечір і вихідні, потім на години пік, коли лінія зайнята. Детальніше про це в статті [«Як не втрачати дзвінки після 18:00»](/blog/yak-ne-vtrachaty-dzvinky-pislya-18).',
      ],
    },
    {
      h2: 'Що бачить реєстратура',
      blocks: [
        'Кожен дзвінок потрапляє в журнал кабінету: хто дзвонив, скільки тривала розмова, чим закінчилась. Розмову можна прослухати і прочитати текстом, а записи з’являються в календарі лікаря одразу. Нові записи й заявки приходять сповіщенням [у Telegram](/funktsii/zayavky-v-telegram), тож реєстратура бачить їх, навіть не відкриваючи кабінет.',
        'Пацієнтів, які телефонують повторно, можна вести в контактах: з історією дзвінків, записів і заявок.',
      ],
    },
    {
      h2: 'Дані пацієнтів',
      blocks: [
        'Записи розмов і дані пацієнтів зберігаються в захищеному сховищі, дані кожної клініки ізольовані від інших. Співробітники бачать лише розділи, до яких ви дали доступ. На початку кожного дзвінка звучить попередження про запис і віртуального асистента. Детальніше в [політиці конфіденційності](/privacy).',
      ],
    },
  ],
  faq: [
    {
      q: 'Чи зможе агент записати до конкретного лікаря?',
      a: 'Так. Для кожного лікаря чи кабінету створюється окремий календар з графіком. Агент питає, до кого записатися, або пропонує найближчий вільний час у будь-якого лікаря потрібного профілю.',
    },
    {
      q: 'Що буде, якщо пацієнт телефонує з гострим болем?',
      a: 'Ви визначаєте це в інструкціях агента: наприклад, одразу переводити такі дзвінки на адміністратора, а якщо ніхто не відповідає, прийняти заявку і відразу надіслати її в Telegram.',
    },
    {
      q: 'Ми вже записуємо пацієнтів в іншій програмі. Як бути?',
      a: 'Записи й заявки з Anytel передаються у вашу систему через [вебхуки та API](/funktsii/integratsiya-z-crm). Якщо ваша програма їх не приймає, агент може не записувати сам, а збирати заявку: ім’я, телефон, бажаний час. Адміністратор підтверджує запис уже у своїй програмі.',
    },
    {
      q: 'Чи потрібно змінювати номер клініки?',
      a: 'Ні. Дзвінки переадресовуються з наявного номера або вашої АТС, пацієнти телефонують туди ж, куди й раніше.',
    },
  ],
  related: ['/funktsii/ai-agent-dlya-dzvinkiv', '/funktsii/zayavky-v-telegram', '/dlya/saloniv-krasy', '/blog/skilky-dzvinkiv-vy-propuskayete'],
}

export const meta = contentMeta(page)

export default function Page() {
  return <ContentPage page={page} />
}
